import { cn } from "../../lib/utils";

type Props = {
  selected: boolean;
  onSelect: () => void;
  eyebrow: string;
  title: string;
  description: string;
  disabled?: boolean;
};

/** Shared full-exam hero card. Radio-selectable; selected paints the accent
 *  border + accent-soft fill. Used by both the spreken and schrijven bodies. */
export function ModeHero({ selected, onSelect, eyebrow, title, description, disabled }: Props) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      disabled={disabled}
      className={cn(
        "w-full text-left rounded-[var(--radius-lg)] border-2 p-6 transition-colors",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-[var(--accent)] bg-[var(--accent-soft)]"
          : "border-[var(--border)] bg-[var(--panel)] enabled:hover:border-[var(--border-strong)]",
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <span className="font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)]">
          {eyebrow}
        </span>
        <RadioDot selected={selected} />
      </div>
      <h2 className="mt-3 text-2xl font-semibold tracking-tight">{title}</h2>
      <p className="mt-2.5 font-serif-italic text-[var(--ink-muted)] leading-relaxed max-w-[48ch]">
        {description}
      </p>
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid place-items-center w-6 h-6 rounded-full border-2 flex-none transition-colors",
        selected ? "border-[var(--accent)] bg-[var(--accent)]" : "border-[var(--border-strong)]",
      )}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        className={cn("transition-opacity", selected ? "opacity-100" : "opacity-0")}
      >
        <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
